import { useEffect, useState } from "react";
import { Button, Col, Empty, Form, Input, Modal, Popconfirm, Row, Select, Spin, Table, Tag, Tooltip, message } from "antd";
import { DeleteOutlined, EditOutlined, PaperClipOutlined, PlusOutlined } from "@ant-design/icons";
import "../ClientForm/Form/reviewForm.css";
import "./ClientCustomer.css";
import {
  getCustomerCountryOverview,
  upsertCustomerCountryFee,
  deleteCustomerCountryFee,
  getAllCountries,
  getClientCustomerAttachments,
  getCustomerCountriesWithEmployees,
  updateClientCutomerData,
  unassignCustomerCountryAccountOwner,
} from "../../services/ApiService";
import AccountOwnerChangeModal from "./AccountOwnerChangeModal";
import CountryAccountOwnerChangeModal from "./CountryAccountOwnerChangeModal";

const { Option } = Select;

// Per-country fee overrides for ONE customer -- one level down from the
// client-level ClientCountryFees.js, same "+ Add Country" pattern.
// Confirmed with the user, 2026-10-16: countries here are added
// manually by Finance, never derived from GTN or any other billing
// signal.
//
// Account Owners by Country lives in its OWN section below (confirmed
// with the user, 2026-10-27 -- moved here from a separate "Change
// Account Owner" button/modal, folded into this one instead of a second
// click). Deliberately NOT one merged table with the fees above: fee
// rows only exist for countries Finance explicitly added an override
// for, while owner rows cover every country this customer's employees
// are actually tagged with (a broader, different set) -- combining them
// into one table would either hide owner management for a country with
// no fee override yet, or show a fee row for a country that never had
// one. Two correct, separate sections in one modal instead.
//
// The Add/Edit Country Fees sub-modal is deliberately styled to match
// the existing "Edit Customer Details" form (Form.Item + input-box,
// a 3-column grid, View Attachments + primary submit at the bottom) --
// confirmed with the user, 2026-10-19, rather than a generic antd Modal
// form.
const FEE_FIELDS = [
  { key: "client_deposit", label: "Deposit" },
  { key: "client_setup_fees", label: "Setup Fees" },
  { key: "client_termination_fees", label: "Termination Fees" },
  { key: "client_monthly_minimum_fees_locals", label: "Monthly Min Fees (Locals)" },
  { key: "client_monthly_minimum_fees_expats", label: "Monthly Min Fees (Expats)" },
  { key: "client_monthly_percentage", label: "Monthly %" },
  { key: "client_year_end_charges", label: "Year End Charges" },
  { key: "vat_on_invoices", label: "VAT on Invoices" },
  { key: "vat_applied_on", label: "VAT Applied On" },
  { key: "client_off_cycle_fees", label: "Off Cycle Fees" },
  { key: "client_bank_charges", label: "Bank Charges" },
  { key: "client_it_procurement_charges", label: "IT Procurement Charges" },
  { key: "client_adhoc_service_description", label: "Adhoc Service Description" },
  { key: "recruitment_charges", label: "Recruitment Charges" },
  { key: "visa_sponsorship", label: "Visa Sponsorship" },
  { key: "client_adhoc_service_charges", label: "Adhoc Service Charges" },
];

const EMPTY_FORM = FEE_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), { country_id: undefined });

const SOURCE_LABELS = {
  customer: { text: "This customer", color: "purple" },
  client_country: { text: "From client (country)", color: "blue" },
  client_base: { text: "From client (default)", color: "default" },
  none: { text: "Not set", color: "default" },
};

const CustomerCountryFeesModal = ({ open, customer, canEdit, accountOwnerOptions, onClose, onOverallOwnerSaved }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null); // null = adding a new country
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [attachments, setAttachments] = useState([]);
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  // Account Owners by Country -- see module docstring for why this is a
  // separate section/row-set from the fees table above, not merged.
  const [ownerCountries, setOwnerCountries] = useState([]);
  const [ownerLoading, setOwnerLoading] = useState(true);
  const [unassigning, setUnassigning] = useState(null); // the row "key" currently being unassigned, or null
  const [overallChangeModal, setOverallChangeModal] = useState(null); // {customerId, newOwnerId, newOwnerLabel, label}
  const [countryChangeModal, setCountryChangeModal] = useState(null); // {customerId, countryId, countryName, newOwnerId, newOwnerLabel}

  // `customer` is the merged (by name) entity from the Customer List --
  // same-named customers across different countries are stored as
  // multiple ClientCustomerForm rows underneath (confirmed with the
  // user, 2026-10-27), but fees/attachments/owners are all still
  // per-row, so every fetch below loops `customer.ids`/`customer.rows`
  // and tags each result with the real underlying id it came from.
  const customerIds = customer?.ids || (customer?.id ? [customer.id] : []);
  const customerRows = customer?.rows || (customer?.id ? [{ id: customer.id, country: null, account_owner: customer.account_owner }] : []);

  const rowLabel = (row, idx) =>
    row.country || (customerRows.length > 1 ? `Entry ${idx + 1} (no country set yet)` : "This customer");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(customerIds.map((id) => getCustomerCountryOverview(id)));
      const merged = [];
      results.forEach((res, idx) => {
        if (res?.responseCode === 200) {
          (res.countries || []).forEach((row) => merged.push({ ...row, _customerId: customerIds[idx] }));
        } else {
          message.error(res?.responseMessage || "Could not load this customer's country fees.");
        }
      });
      setRows(merged);
    } catch (error) {
      console.error("Error fetching customer country fees:", error);
      message.error("Could not load this customer's country fees.");
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnerCountries = async () => {
    setOwnerLoading(true);
    try {
      const results = await Promise.all(customerIds.map((id) => getCustomerCountriesWithEmployees(id)));
      const merged = [];
      results.forEach((res, idx) => {
        if (res?.responseCode === 200) {
          (res.countries || []).forEach((row) => merged.push({ ...row, _customerId: customerIds[idx] }));
        } else {
          message.error(res?.responseMessage || "Could not load this customer's countries.");
        }
      });
      setOwnerCountries(merged);
    } catch (error) {
      console.error("Error fetching customer countries:", error);
      message.error("Could not load this customer's countries.");
    } finally {
      setOwnerLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !customer) return;
    fetchRows();
    fetchOwnerCountries();

    (async () => {
      try {
        const res = await getAllCountries(1);
        const options = (res?.country_list || [])
          .map((c) => ({ id: c.id, name: (c.country_name || "").split("(")[0].trim() }))
          .filter((c) => c.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setCountryOptions(options);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer]);

  const openAddModal = () => {
    setEditingRow(null);
    setFormValues({ ...EMPTY_FORM, _customerId: customerIds[0] });
    setFormModalOpen(true);
  };

  const openEditModal = (row) => {
    setEditingRow(row);
    setFormValues({
      country_id: row.country_id,
      _customerId: row._customerId,
      ...FEE_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: row.fees[f.key]?.value || "" }), {}),
    });
    setFormModalOpen(true);
  };

  const handleSave = async () => {
    if (!formValues.country_id) {
      message.error("Country is required.");
      return;
    }
    setSaving(true);
    try {
      const res = await upsertCustomerCountryFee(
        formValues._customerId || customerIds[0],
        formValues.country_id,
        formValues
      );
      if (res?.responseCode === 200) {
        message.success(editingRow ? "Country fee updated" : "Country fee added");
        setFormModalOpen(false);
        fetchRows();
      } else {
        message.error(res?.responseMessage || "Failed to save country fee.");
      }
    } catch (error) {
      console.error("Error saving customer country fee:", error);
      message.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      const res = await deleteCustomerCountryFee(row._customerId, row.country_id);
      if (res?.responseCode === 200) {
        message.success("Country fee removed");
        fetchRows();
      } else {
        message.error(res?.responseMessage || "Failed to remove country fee.");
      }
    } catch (error) {
      console.error("Error removing customer country fee:", error);
      message.error("Something went wrong. Please try again.");
    }
  };

  const fetchAndShowAttachments = async () => {
    setAttachmentsLoading(true);
    try {
      const results = await Promise.all(customerIds.map((id) => getClientCustomerAttachments(id)));
      const merged = [];
      let anyFailed = false;
      results.forEach((res) => {
        if (res?.responseCode === 200) merged.push(...(res.attachments || []));
        else anyFailed = true;
      });
      if (anyFailed) message.error("Some attachments could not be loaded.");
      setAttachments(merged);
      setAttachmentsModalOpen(true);
    } catch (error) {
      console.error("Error fetching attachments:", error);
      message.error("Error fetching attachments");
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const ownerLabel = (ownerId) =>
    accountOwnerOptions.find((o) => o.value === ownerId)?.label || ownerId || "Unassigned";

  // Restores the old inline-dropdown "clear" behavior -- immediate, no
  // employee-reassignment prompt, since there's no one to move employees
  // to.
  const handleUnassignOverall = async (customerId, rowKey) => {
    setUnassigning(rowKey);
    try {
      const formDataPayload = new FormData();
      formDataPayload.append("data", JSON.stringify({ account_owner: null }));
      const res = await updateClientCutomerData(customerId, formDataPayload);
      if (res?.responseCode === 200) {
        message.success("Account owner unassigned");
        onOverallOwnerSaved?.(null);
      } else {
        message.error(res?.responseMessage || "Failed to unassign account owner.");
      }
    } catch (error) {
      console.error("Error unassigning account owner:", error);
      message.error("Something went wrong. Please try again.");
    } finally {
      setUnassigning(null);
    }
  };

  const handleUnassignCountry = async (customerId, countryId, rowKey) => {
    setUnassigning(rowKey);
    try {
      const res = await unassignCustomerCountryAccountOwner(customerId, countryId);
      if (res?.responseCode === 200) {
        message.success("Account owner unassigned");
        fetchOwnerCountries();
      } else {
        message.error(res?.responseMessage || "Failed to unassign account owner.");
      }
    } catch (error) {
      console.error("Error unassigning country account owner:", error);
      message.error("Something went wrong. Please try again.");
    } finally {
      setUnassigning(null);
    }
  };

  const renderOwnerSelect = (currentOwnerId, onPick) => (
    <Select
      style={{ width: 180 }}
      placeholder="Unassigned"
      value={currentOwnerId || undefined}
      disabled={!canEdit}
      onChange={(value) => onPick(value, ownerLabel(value))}
      showSearch
      optionFilterProp="children"
    >
      {accountOwnerOptions.map((o) => (
        <Option key={o.value} value={o.value}>
          {o.label}
        </Option>
      ))}
    </Select>
  );

  const ownerRows = [
    ...customerRows.map((row, idx) => ({
      key: `overall-${row.id}`,
      isOverall: true,
      label: customerRows.length > 1 ? `Overall — ${rowLabel(row, idx)}` : "Overall (customer default)",
      current_account_owner: row.account_owner,
      _customerId: row.id,
    })),
    ...ownerCountries.map((c) => ({ key: `country-${c._customerId}-${c.country_id}`, ...c })),
  ];

  const ownerColumns = [
    {
      title: "Scope",
      key: "scope",
      render: (_, row) => (row.isOverall ? row.label : row.country_name),
    },
    {
      title: "Current Owner",
      key: "owner",
      render: (_, row) =>
        row.isOverall
          ? renderOwnerSelect(row.current_account_owner, (value, label) =>
              value &&
              setOverallChangeModal({
                customerId: row._customerId,
                label: row.label,
                newOwnerId: value,
                newOwnerLabel: label,
              })
            )
          : renderOwnerSelect(row.current_account_owner, (value, label) =>
              value &&
              setCountryChangeModal({
                customerId: row._customerId,
                countryId: row.country_id,
                countryName: row.country_name,
                newOwnerId: value,
                newOwnerLabel: label,
              })
            ),
    },
    {
      title: "",
      key: "unassign",
      render: (_, row) =>
        canEdit && row.current_account_owner ? (
          <Popconfirm
            title={`Unassign the owner for ${row.isOverall ? row.label : row.country_name}?`}
            onConfirm={() =>
              row.isOverall
                ? handleUnassignOverall(row._customerId, row.key)
                : handleUnassignCountry(row._customerId, row.country_id, row.key)
            }
            okText="Unassign"
            cancelText="Cancel"
          >
            <Button size="small" danger loading={unassigning === row.key}>
              Unassign
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  const columns = [
    { title: "Country", dataIndex: "country_name", key: "country_name" },
    {
      title: "Setup Fees",
      key: "client_setup_fees",
      render: (_, row) => {
        const fee = row.fees.client_setup_fees;
        const src = SOURCE_LABELS[fee.source];
        return (
          <Tooltip title={src.text}>
            <span>{fee.value || "—"} <Tag color={src.color}>{src.text}</Tag></span>
          </Tooltip>
        );
      },
    },
    {
      title: "Deposit",
      key: "client_deposit",
      render: (_, row) => row.fees.client_deposit.value || "—",
    },
    {
      title: "Monthly Min Fees (Locals)",
      key: "client_monthly_minimum_fees_locals",
      render: (_, row) => row.fees.client_monthly_minimum_fees_locals.value || "—",
    },
  ];

  if (canEdit) {
    columns.push({
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <>
          <Button type="link" icon={<EditOutlined />} onClick={() => openEditModal(row)} />
          <Popconfirm
            title="Remove this country's fee entry?"
            onConfirm={() => handleDelete(row)}
            okText="Remove"
            cancelText="Cancel"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </>
      ),
    });
  }

  return (
    <>
      <Modal
        title={`Country Fees & Account Owners — ${customer?.client_customer_name || ""}`}
        open={open}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
        width={960}
      >
        <h4 style={{ marginTop: 0, marginBottom: "0.25rem" }}>Country Fees</h4>
        <p style={{ color: "#888", fontSize: "0.85rem", marginTop: 0, marginBottom: "1rem" }}>
          Overrides this customer's fees for a specific country -- falls back to the client's own
          country fee, then the client's default, if not set here.
        </p>

        {canEdit && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.75rem" }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
              Add Country
            </Button>
          </div>
        )}

        <Spin spinning={loading}>
          <Table
            rowKey={(row) => `${row._customerId}-${row.country_id}`}
            dataSource={rows}
            columns={columns}
            pagination={false}
            locale={{ emptyText: "No country-specific fees added yet." }}
            size="small"
          />
        </Spin>

        <h4 style={{ marginTop: "1.5rem", marginBottom: "0.25rem" }}>Account Owners by Country</h4>
        <p style={{ color: "#888", fontSize: "0.85rem", marginTop: 0, marginBottom: "1rem" }}>
          "Overall" is this customer's default owner. Each country below (derived from this
          customer's own employees' Country of Employment) can have its own owner instead.
        </p>
        <Spin spinning={ownerLoading}>
          <Table rowKey="key" dataSource={ownerRows} columns={ownerColumns} pagination={false} size="small" />
        </Spin>
      </Modal>

      <Modal
        title={editingRow ? `Edit Fees — ${editingRow.country_name}` : "Add Country Fees"}
        open={formModalOpen}
        onCancel={() => setFormModalOpen(false)}
        footer={null}
        width={800}
        centered
      >
        <Spin spinning={saving} size="large" tip="Saving...">
          <div className="Updatesection custom-scrollbar">
            <Form layout="vertical">
              <Row gutter={[10, 10]}>
                {customerRows.length > 1 && (
                  <Col span={8}>
                    <Form.Item label="Customer Entry">
                      <Select
                        className="input-box"
                        value={formValues._customerId}
                        onChange={(value) => setFormValues((prev) => ({ ...prev, _customerId: value }))}
                        disabled={!!editingRow}
                        optionFilterProp="children"
                      >
                        {customerRows.map((row, idx) => (
                          <Option key={row.id} value={row.id}>
                            {rowLabel(row, idx)}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                <Col span={8}>
                  <Form.Item label="Country">
                    <Select
                      className="input-box"
                      value={formValues.country_id}
                      onChange={(value) => setFormValues((prev) => ({ ...prev, country_id: value }))}
                      placeholder="Select country"
                      showSearch
                      disabled={!!editingRow}
                      optionFilterProp="children"
                    >
                      {countryOptions.map((c) => (
                        <Option key={c.id} value={c.id}>
                          {c.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                {FEE_FIELDS.map((field) => (
                  <Col span={8} key={field.key}>
                    <Form.Item label={field.label}>
                      <Input
                        className="input-box"
                        value={formValues[field.key]}
                        placeholder={field.label}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </Form>
          </div>

          <div className="button-container">
            <Button
              type="dashed"
              loading={attachmentsLoading}
              onClick={fetchAndShowAttachments}
              icon={<PaperClipOutlined />}
              style={{ marginRight: "1rem", color: "#1890ff", borderColor: "#1890ff" }}
            >
              View Attachments
            </Button>
            <Button type="primary" onClick={handleSave}>
              {editingRow ? "Save Changes" : "Add Country Fees"}
            </Button>
          </div>
        </Spin>
      </Modal>

      <Modal
        title="Client Customer Attachments"
        open={attachmentsModalOpen}
        onCancel={() => setAttachmentsModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setAttachmentsModalOpen(false)}>
            Close
          </Button>,
        ]}
        centered
      >
        {attachments.length > 0 ? (
          <ul style={{ listStyleType: "none", padding: 0 }}>
            {attachments.map((file, idx) => (
              <li
                key={idx}
                style={{ marginBottom: "1rem", padding: "0.5rem", border: "1px solid #eee", borderRadius: "5px" }}
              >
                <strong>{file.type}: </strong>
                <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ color: "#1890ff" }}>
                  {file.name}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <Empty description="No attachments found for this customer." />
        )}
      </Modal>

      <AccountOwnerChangeModal
        open={!!overallChangeModal}
        customer={
          overallChangeModal && {
            id: overallChangeModal.customerId,
            client_customer_name: customer?.client_customer_name,
            account_owner: customerRows.find((r) => r.id === overallChangeModal.customerId)?.account_owner,
          }
        }
        newOwnerId={overallChangeModal?.newOwnerId}
        newOwnerLabel={overallChangeModal?.newOwnerLabel}
        onClose={() => setOverallChangeModal(null)}
        onSaved={(newOwnerId) => onOverallOwnerSaved?.(newOwnerId)}
      />

      <CountryAccountOwnerChangeModal
        open={!!countryChangeModal}
        customerId={countryChangeModal?.customerId}
        customerName={customer?.client_customer_name}
        countryId={countryChangeModal?.countryId}
        countryName={countryChangeModal?.countryName}
        newOwnerId={countryChangeModal?.newOwnerId}
        newOwnerLabel={countryChangeModal?.newOwnerLabel}
        onClose={() => setCountryChangeModal(null)}
        onSaved={() => fetchOwnerCountries()}
      />
    </>
  );
};

export default CustomerCountryFeesModal;
